import React from 'react';
import { Coffee, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../features/language/useLanguage';

/**
 * The bridge shown before step 1 — a friendly hello that sets expectations
 * before the hands-on tour begins. Pinned to the top-right corner.
 */
interface OnboardingWelcomeCardProps {
  onStart: () => void;
  onSkip: () => void;
}

export const OnboardingWelcomeCard: React.FC<OnboardingWelcomeCardProps> = ({ onStart, onSkip }) => {
  const { t } = useLanguage();
  return (
    <div
      className="w-full overflow-hidden rounded-xl border border-amber-200 bg-white shadow-2xl"
      data-test-id="onboarding-welcome"
    >
      <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-amber-600 to-orange-700" />
      <div className="p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Coffee className="h-5 w-5" />
          </span>
          <h2 className="text-lg font-semibold text-gray-900">{t('onboarding.welcome.title', undefined, 'Welcome to Qrema')}</h2>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-gray-700">
          {t(
            'onboarding.welcome.intro',
            undefined,
            "Let's set up your roastery together. In seven quick steps you'll add your real stock, run a practice purchase, roast, cup and sell, then ask Qora about your numbers.",
          )}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-gray-500">
          {t(
            'onboarding.welcome.disclaimer',
            undefined,
            'Your inventory is real and stays. The practice records are cleared at the end — nothing to undo.',
          )}
        </p>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            data-test-id="onboarding-welcome-skip"
            onClick={onSkip}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            {t('onboarding.welcome.skip', undefined, 'Skip tour')}
          </button>
          <button
            type="button"
            data-test-id="onboarding-welcome-start"
            onClick={onStart}
            className="onboarding-btn-glow inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            {t('onboarding.welcome.start', undefined, 'Start tour')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
